import { useCallback, useMemo } from 'react';
import type { SettingsPageDataAndHandlers } from '../context';
import { useProjectSettingsContext } from '../context';
import { useAsyncRequest } from '../../../../hooks';
import { get } from '../../../../redux/api';
import { selectOptionFactory } from '../../../../utils';
import { Category } from '../../../../../shared/issuesV2';
import {
  useCanUserSetLicensingIssueFilter,
  useCanUserSetLicensingPolicy,
  useCanUserSetQualityIssueFilter,
  useCanUserSetQualityPolicy,
  useCanUserSetSecurityIssueFilter,
  useCanUserSetSecurityPolicy,
  useCanUserSetSomeIssueFilter,
  useCanUserSetSomeIssuePolicy,
} from '../permissions';
import type { GenericIssueSettingsProps, ProjectIssueSettings } from './types';
import type { GetAllFiltersResponse, NamedFilter } from '../../../../../shared/savedFilters';
import { usePolicies } from '../../../shared/policies/hooks';
import { useIsQualityEnabled, useIsSecurityEnabled } from '../../../../helpers/features';
import { UNSET_POLICY_OPTION } from './constants';

const issueFiltersEndpoint = '/api/issue-filters';

function fetchLicensingIssueFilters() {
  return get<GetAllFiltersResponse>(`${issueFiltersEndpoint}?category=${Category.Licensing}`);
}

function fetchSecurityIssueFilters() {
  return get<GetAllFiltersResponse>(`${issueFiltersEndpoint}?category=${Category.Vulnerability}`);
}

function fetchQualityIssueFilters() {
  return get<GetAllFiltersResponse>(`${issueFiltersEndpoint}?category=${Category.Quality}`);
}

//  This is used as a fallback if security or quality is not enabled.
function fetchNothing() {
  return Promise.resolve({ data: undefined } as Response & { data?: GetAllFiltersResponse });
}

export const UNSET_FILTER_OPTION = selectOptionFactory(null, 'All issues (not filtered)');

function mapFilterToSelectOption(filter: NamedFilter<Category>) {
  return selectOptionFactory(filter.id, filter.name);
}

//  Generic hook for fetching each of the issue filters.
function useFilters(
  fetchFilters: () => Promise<Response & { data?: GetAllFiltersResponse }>,
  filterId?: number | null,
) {
  const { isLoading, value: filters } = useAsyncRequest(fetchFilters);

  const filterOptions = useMemo(() => [
    UNSET_FILTER_OPTION,
    ...(filters?.map(mapFilterToSelectOption) ?? []),
  ], [filters]);

  return {
    isLoading,
    filterOptions,
    selectedFilterOption: useMemo(() => filterId === null
      ? UNSET_FILTER_OPTION
      : filterOptions?.find((filter) => filter.value === filterId) ?? UNSET_FILTER_OPTION, [filterOptions, filterId]),
  };
}

type UseProjectIssuesSettingsReturn = Pick<SettingsPageDataAndHandlers<ProjectIssueSettings>, 'isDirty' | 'isSaving' | 'onUpdate' | 'onSave' | 'projectSettingsBaseURL'> & {
  isLoading: boolean;
  isSaveButtonDisabled: boolean;
  shouldShowIssues: boolean;
} & {
  licensing: GenericIssueSettingsProps;
  security: GenericIssueSettingsProps;
  quality: GenericIssueSettingsProps;
};

/**
 * This is the hook for the issue settings page.
 * It pulls in the editable fields from the project context,
 * along with helpers for updating and saving the data.
 *
 * It also fetches other data which the page depends on and provides
 * it in the return value. Issue settings depends on:
 * - Issue filters (for each category which is enabled).
 * - Policies list.
 */
const useProjectIssuesSettings = (): UseProjectIssuesSettingsReturn => {
  const {
    issues: {
      locator,
      settings,
      isDirty,
      isSaving,
      onSave,
      onUpdate,
      canUserEditSettings,
      projectSettingsBaseURL,
    },
  } = useProjectSettingsContext();

  const hasSecurity = useIsSecurityEnabled();
  const hasQuality = useIsQualityEnabled();

  const { policies, isLoading: isFetchingPolicies } = usePolicies();
  const licensingFilters = useFilters(
    fetchLicensingIssueFilters,
    settings.filters[Category.Licensing],
  );
  const securityFilters = useFilters(
    //  Don't fetch security filters if security is not enabled.
    hasSecurity ? fetchSecurityIssueFilters : fetchNothing,
    settings.filters[Category.Vulnerability],
  );
  const qualityFilters = useFilters(
    //  Don't fetch quality filters if security is not enabled.
    hasQuality ? fetchQualityIssueFilters : fetchNothing,
    settings.filters[Category.Quality],
  );

  const isLoading = isFetchingPolicies ||
    licensingFilters.isLoading ||
    securityFilters.isLoading ||
    qualityFilters.isLoading;

  //  Permissions checks
  const canSetSomeIssuePolicy = useCanUserSetSomeIssuePolicy(locator);
  const canSetSomeIssueFilter = useCanUserSetSomeIssueFilter(locator);
  const shouldShowIssues = canUserEditSettings || canSetSomeIssuePolicy || canSetSomeIssueFilter;

  return {
    isDirty,
    isSaving,
    onSave,
    onUpdate,
    isLoading,
    shouldShowIssues,
    projectSettingsBaseURL,
    isSaveButtonDisabled: !isDirty || isLoading || isSaving,
    licensing: {
      isScanningEnabled: settings.licensingIssueScanningEnabled,
      policyOptions: policies[Category.Licensing],
      selectedPolicyOption: useMemo(() => policies[Category.Licensing].find((policy) =>
        policy.value === settings.policyId) ?? UNSET_POLICY_OPTION, [policies, settings.policyId]),
      isCICheckEnabled: settings.licensingStatusCheckEnabled,
      selectedFilterOption: licensingFilters.selectedFilterOption,
      filterOptions: licensingFilters.filterOptions,
      onToggleIsScanningEnabled: useCallback((isChecked: boolean) =>
        onUpdate({ licensingIssueScanningEnabled: isChecked }), [onUpdate]),
      onSelectedPolicyChange: useCallback((policyId: number | null) =>
        onUpdate({ policyId: policyId || null }), [onUpdate]),
      onToggleIsCICheckEnabled: useCallback((isChecked: boolean) =>
        onUpdate({ licensingStatusCheckEnabled: isChecked }), [onUpdate]),
      onSelectedFilterChange: useCallback((filterId: number | null) =>
        onUpdate({ filters: {
          ...settings.filters,
          [Category.Licensing]: filterId || null,
        } }), [onUpdate, settings.filters]),
      canEditPolicy: useCanUserSetLicensingPolicy(locator),
      canEditFilters: useCanUserSetLicensingIssueFilter(locator),
    },
    security: {
      isScanningEnabled: settings.securityIssueScanningEnabled,
      policyOptions: policies[Category.Vulnerability],
      selectedPolicyOption: useMemo(() => policies[Category.Vulnerability].find((policy) =>
        policy.value === settings.securityPolicyId) ?? UNSET_POLICY_OPTION, [policies, settings.securityPolicyId]),
      isCICheckEnabled: settings.securityStatusCheckEnabled,
      selectedFilterOption: securityFilters.selectedFilterOption,
      filterOptions: securityFilters.filterOptions,
      onToggleIsScanningEnabled: useCallback((isChecked: boolean) =>
        onUpdate({ securityIssueScanningEnabled: isChecked }), [onUpdate]),
      onSelectedPolicyChange: useCallback((policyId: number | null) =>
        onUpdate({ securityPolicyId: policyId || null }), [onUpdate]),
      onToggleIsCICheckEnabled: useCallback((isChecked: boolean) =>
        onUpdate({ securityStatusCheckEnabled: isChecked }), [onUpdate]),
      onSelectedFilterChange: useCallback((filterId: number | null) =>
        onUpdate({ filters: {
          ...settings.filters,
          [Category.Vulnerability]: filterId || null,
        } }), [onUpdate, settings.filters]),
      canEditPolicy: useCanUserSetSecurityPolicy(locator),
      canEditFilters: useCanUserSetSecurityIssueFilter(locator),
    },
    quality: {
      isScanningEnabled: settings.qualityIssueScanningEnabled,
      policyOptions: policies[Category.Quality],
      selectedPolicyOption: useMemo(() => policies[Category.Quality].find((policy) =>
        policy.value === settings.qualityPolicyId) ?? UNSET_POLICY_OPTION, [policies, settings.qualityPolicyId]),
      isCICheckEnabled: settings.qualityStatusCheckEnabled,
      selectedFilterOption: qualityFilters.selectedFilterOption,
      filterOptions: qualityFilters.filterOptions,
      onToggleIsScanningEnabled: useCallback((isChecked: boolean) =>
        onUpdate({ qualityIssueScanningEnabled: isChecked }), [onUpdate]),
      onSelectedPolicyChange: useCallback((policyId: number | null) =>
        onUpdate({ qualityPolicyId: policyId || null }), [onUpdate]),
      onToggleIsCICheckEnabled: useCallback((isChecked: boolean) =>
        onUpdate({ qualityStatusCheckEnabled: isChecked }), [onUpdate]),
      onSelectedFilterChange: useCallback((filterId: number | null) =>
        onUpdate({ filters: {
          ...settings.filters,
          [Category.Quality]: filterId || null,
        } }), [onUpdate, settings.filters]),
      canEditPolicy: useCanUserSetQualityPolicy(locator),
      canEditFilters: useCanUserSetQualityIssueFilter(locator),
    },
  };
};

export default useProjectIssuesSettings;
